import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import GavelIcon from '@mui/icons-material/Gavel';
import { Badge, formatAlertTimestamp } from '../pages/markets/components/shared';
import type { AlertItem } from '../pages/markets/api/types';

interface GateAlertContext {
    account_id?: string;
    condition_id?: string;
    market_slug?: string;
    proposed_limit_usd?: number;
    request_generation?: number;
}

/**
 * Renders a gate_request alert: "approval requested" with account, market
 * and the proposed exposure. Clicking the row (handled by NotificationBell)
 * deep-links to /gates?account=…&market=….
 */
export function GateNotificationRow({ alert }: { alert: AlertItem }) {
    const theme = useTheme();
    const ctx = (alert.context ?? {}) as GateAlertContext;

    return (
        <Box sx={{ px: 1.25, py: 0.75 }}>
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'baseline',
                    gap: 1,
                }}
            >
                <Typography
                    variant="caption"
                    sx={{
                        fontWeight: 600,
                        color: 'text.primary',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                    }}
                >
                    <GavelIcon sx={{ fontSize: 13 }} /> Approval requested
                </Typography>
                <Typography
                    sx={{
                        fontSize: '10px',
                        color: 'text.disabled',
                        fontFamily: 'monospace',
                        flexShrink: 0,
                    }}
                >
                    {formatAlertTimestamp(alert.ts)}
                </Typography>
            </Box>
            {ctx.market_slug && (
                <Typography
                    sx={{
                        fontSize: '11px',
                        fontWeight: 500,
                        color: 'text.primary',
                        mt: 0.25,
                    }}
                >
                    {ctx.market_slug}
                </Typography>
            )}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.75,
                    mt: 0.4,
                }}
            >
                <Badge color={theme.palette.warning.main}>GATE</Badge>
                {ctx.proposed_limit_usd != null && (
                    <Typography
                        component="span"
                        sx={{
                            fontSize: '10px',
                            fontFamily: 'monospace',
                            color: 'text.primary',
                        }}
                    >
                        $
                        {ctx.proposed_limit_usd.toLocaleString('en-US', {
                            maximumFractionDigits: 0,
                        })}{' '}
                        proposed
                    </Typography>
                )}
                {ctx.account_id && (
                    <Typography
                        component="span"
                        sx={{
                            ml: 'auto',
                            fontSize: '9px',
                            fontFamily: 'monospace',
                            color: 'text.disabled',
                        }}
                    >
                        {ctx.account_id}
                    </Typography>
                )}
            </Box>
        </Box>
    );
}
